/**
 * The Daily log's shape (LED-1152, design "Daily log" / "Daily log · detail"),
 * as pure functions so the page's arithmetic can be asserted without a DOM.
 *
 * The page draws a day as:
 *  - one Manpower table (who was on site), and
 *  - one card per walk: a header, ONE Photos row for that walk's photos, then
 *    the walk's other entries — or, with "Group by walk" off, one flat
 *    TIME / TYPE / ENTRY / CREATOR table where photos still roll up to one row
 *    per walk.
 *
 * Accepted Photos cards roll up into a gallery when one is available;
 * otherwise they remain entries. Pending cards always remain reviewable.
 * The photos the gallery shows are the walk's photos that
 * landed on the day's log — on an accepted Photos card, on an accepted
 * daily-log entry, or on no record at all (those go out with the day's log),
 * never one kept out of sync or still waiting in review.
 */

import type { ConsoleRow, ConsoleWalkPhoto, DailyLogWalkRow } from "../api";

/** The chip set: `All`, then each category that logged something. */
export type DailyLogFilter = string | null;

/** A row of the walk's Photos — one per walk, never one per photo. */
export interface DailyLogPhotosRow {
  type: "photos";
  walk: DailyLogWalkRow;
  photos: ConsoleWalkPhoto[];
  /** When the first photo was taken (ISO), for the row's time. */
  at: string;
}

export interface DailyLogEntryRow {
  type: "entry";
  row: ConsoleRow;
  walk: DailyLogWalkRow | null;
}

export type DailyLogTimelineRow = DailyLogPhotosRow | DailyLogEntryRow;

export interface DailyLogWalkGroup {
  walk: DailyLogWalkRow | null;
  photos: DailyLogPhotosRow | null;
  entries: DailyLogEntryRow[];
}

/** Is this walk photo on the day's log? See the file header. */
export function isLoggedWalkPhoto(photo: ConsoleWalkPhoto): boolean {
  if (photo.type !== "image" || photo.syncExcluded) return false;
  const home = photo.home;
  if (!home) return true;
  if (!home.approved) return false;
  if (home.kind === "photos") return true;
  return home.kind === "log" && home.category !== "observations";
}

/** The walk's photos on the log, oldest first. */
export function loggedPhotos(photos: ConsoleWalkPhoto[] | undefined): ConsoleWalkPhoto[] {
  return (photos ?? [])
    .filter(isLoggedWalkPhoto)
    .sort((a, b) => Date.parse(a.timestamp) - Date.parse(b.timestamp));
}

/** Photos cards normally roll up into the walk's gallery. */
export function isPhotosEntry(row: ConsoleRow): boolean {
  return row.category === "photos";
}

/** A manpower entry colliding with a row already on the day's Procore log
 *  that nobody has chosen an outcome for yet (LED-1156's CONFLICT tag). */
export function hasOpenManpowerConflict(row: ConsoleRow): boolean {
  const merge = row.category === "manpower" ? row.manpowerMerge : undefined;
  return !!merge && !merge.resolution && merge.candidates.length > 0;
}

/** `"Delivery"`, not `"Deliveries"` — the tag names ONE entry. */
export function singularLabel(label: string): string {
  return label.replace(/ies$/, "y").replace(/s$/, "");
}

/**
 * The walk groups for the day, in the server's walk order (newest first).
 *
 * `photosByWalk` is the walk-photos read per walk; a walk whose photos have
 * not arrived (or could not be read) falls back to the photos its accepted
 * Photos card rows carry in `previewPhotos`, so the row is never missing just
 * because the second read was slow.
 */
export function buildWalkGroups(args: {
  walks: DailyLogWalkRow[];
  rows: ConsoleRow[];
  photosByWalk: ReadonlyMap<string, ConsoleWalkPhoto[]>;
  showPhotos: boolean;
  passes: (row: ConsoleRow) => boolean;
}): DailyLogWalkGroup[] {
  const { walks, rows, photosByWalk, showPhotos, passes } = args;
  const galleries = new Map(walks.map((walk) => [
    walk.visitId, showPhotos ? photosRowFor(walk, rows, photosByWalk.get(walk.visitId)) : null,
  ]));
  const byVisit = new Map<string, ConsoleRow[]>();
  for (const row of rows) {
    if (row.category === "manpower") continue;
    // Keep counted Photos records accessible when no gallery can represent
    // them (including missing walks/media), and keep pending records reviewable.
    if (isPhotosEntry(row) && row.reviewStatus !== "pending" && galleries.get(row.visitId)) continue;
    if (!passes(row)) continue;
    const bucket = byVisit.get(row.visitId);
    if (bucket) bucket.push(row);
    else byVisit.set(row.visitId, [row]);
  }
  for (const bucket of byVisit.values()) {
    bucket.sort((a, b) => Date.parse(a.createdAt) - Date.parse(b.createdAt));
  }
  const groups: DailyLogWalkGroup[] = [];
  for (const walk of walks) {
    const photos = galleries.get(walk.visitId) ?? null;
    const entries = (byVisit.get(walk.visitId) ?? []).map((row) => ({ type: "entry" as const, row, walk }));
    byVisit.delete(walk.visitId);
    if (photos || entries.length > 0) groups.push({ walk, photos, entries });
  }
  // Rows whose walk the server did not describe: a final group, never dropped.
  const orphans = [...byVisit.values()].flat().sort((a, b) => Date.parse(a.createdAt) - Date.parse(b.createdAt));
  if (orphans.length > 0) {
    groups.push({ walk: null, photos: null, entries: orphans.map((row) => ({ type: "entry" as const, row, walk: null })) });
  }
  return groups;
}

function photosRowFor(
  walk: DailyLogWalkRow,
  rows: ConsoleRow[],
  loaded: ConsoleWalkPhoto[] | undefined,
): DailyLogPhotosRow | null {
  let photos = loaded ? loggedPhotos(loaded) : [];
  if (!loaded) {
    // Fallback: the accepted Photos card rows' own previews.
    photos = rows
      .filter((row) => row.visitId === walk.visitId && isPhotosEntry(row) && row.reviewStatus !== "pending")
      .flatMap((row) => (row.previewPhotos ?? []).map((photo, index): ConsoleWalkPhoto => ({
        mediaId: `${row.id}:${index}`,
        url: photo.url,
        thumbnailUrl: photo.url,
        type: "image",
        timestamp: photo.capturedAt ?? row.createdAt,
        description: "",
        descriptionPending: false,
        home: { kind: "photos", itemId: row.id, title: row.title, approved: true },
      })));
  }
  if (photos.length === 0) return null;
  return { type: "photos", walk, photos, at: photos[0]!.timestamp };
}

/** The flat table: every walk's Photos row and entries, in walk order. */
export function flattenGroups(groups: DailyLogWalkGroup[]): DailyLogTimelineRow[] {
  return groups.flatMap((group) => [
    ...(group.photos ? [group.photos] : []),
    ...group.entries,
  ]);
}

/** Workers, hours each and total for a manpower row — `null` when unset. */
export function manpowerFigures(row: ConsoleRow): { workers: number | null; hours: number | null; total: number | null } {
  const read = (key: string) => {
    const raw = row.fieldValues?.[key];
    if (raw === null || raw === undefined || raw === "") return null;
    const value = typeof raw === "number" ? raw : Number(raw);
    return Number.isFinite(value) ? value : null;
  };
  const workers = read("workers");
  const hours = read("hours");
  return { workers, hours, total: workers !== null && hours !== null ? workers * hours : null };
}

/** The Manpower table's foot: companies, workers and hours summed. */
export function manpowerTotals(rows: ConsoleRow[]): { companies: number; workers: number; total: number } {
  let workers = 0;
  let total = 0;
  for (const row of rows) {
    const figures = manpowerFigures(row);
    workers += figures.workers ?? 0;
    total += figures.total ?? 0;
  }
  const companies = new Set(rows.map((row) => String(row.fieldValues?.vendor ?? row.title).trim().toLowerCase())).size;
  return { companies, workers, total };
}

/** `"4 MIN"` — a walk's recorded length, as the walk header prints it. */
export function walkDurationLabel(walk: DailyLogWalkRow | null): string {
  return walk?.recordedMinutes !== undefined ? `${walk.recordedMinutes} min` : "";
}
